import React, { useState, useEffect, useContext } from 'react';
import {
  Card, Row, Col, Button, Table, Modal, Form,
  Alert, Spinner, Badge, Tabs, Tab
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faCoins, faPlus, faHistory, faDollarSign,
  faArrowUp, faArrowDown, faCheck, faTimes, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

const Wallet = () => {
  const { user } = useContext(MyContext);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(1000);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');

  const loadPaystackScript = () => {
    return new Promise((resolve, reject) => {
      if (window.PaystackPop) {
        setPaystackLoaded(true);
        resolve();
        return;
      }

      if (document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]')) {
        const check = setInterval(() => {
          if (window.PaystackPop) {
            clearInterval(check);
            setPaystackLoaded(true);
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => {
        setPaystackLoaded(true);
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Paystack script'));
      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    if (user) {
      loadWalletData();
    }
    loadPaystackScript().catch(err => console.error('Paystack load error:', err));
  }, [user]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${backendUrl}/wallet.php?email=${encodeURIComponent(user.email)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();
      console.log('Raw API response:', data);

      if (data.success) {
        console.log('Setting wallet data:', data.wallet);
        console.log('Setting transactions:', data.transactions);
        console.log('Setting purchase history:', data.purchaseHistory);
        
        setWallet(data.wallet);
        
        // Use separate data sources
        const transactionsData = data.transactions || [];
        const purchaseHistoryData = data.purchaseHistory || [];
        
        console.log('Transactions data to set:', transactionsData);
        console.log('Purchase history data to set:', purchaseHistoryData);
        
        setPurchaseHistory(purchaseHistoryData);
        setTransactions(transactionsData);
        
        // Debug logging
        console.log('Wallet data received:', data);
        console.log('Transactions received:', data.transactions);
        console.log('Purchase history received:', data.purchaseHistory);
      } else {
        console.error('API error:', data.error || data.messages);
        setError(data.error || 'Failed to load wallet data');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Failed to load wallet data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaystackCallback = async (response) => {
    try {
      const purchaseRes = await fetch(`${backendUrl}/wallet.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          action: 'purchase_coins',
          amount: purchaseAmount * 100,
          transaction_id: response.reference
        })
      });

      const purchaseData = await purchaseRes.json();
      if (!purchaseData.success) throw new Error(purchaseData.error || 'Purchase failed');

      const confirmRes = await fetch(`${backendUrl}/wallet.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          action: 'confirm_purchase',
          transaction_id: response.reference,
          payment_data: {
            reference: response.reference,
            status: response.status,
            transaction: response.transaction,
            message: response.message
          }
        })
      });

      const confirmData = await confirmRes.json();
      if (confirmData.success) {
        setSuccess(`Successfully purchased ${confirmData.coins_added} coins! Your new balance is ${confirmData.new_balance} coins.`);
        setShowPurchaseModal(false);
        loadWalletData();
      } else {
        throw new Error(confirmData.error || 'Confirmation failed');
      }
    } catch (err) {
      console.error('Paystack callback error:', err);
      setError('Payment went through but updating your wallet failed. Reference: ' + response.reference);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setPurchaseLoading(true);
      setError(null);

      if (!paystackLoaded) await loadPaystackScript();
      if (!window.PaystackPop) throw new Error('Paystack not available. Check internet connection.');

      const publicKey = 'pk_live_aceb142e35c8ebef0fd2079c78151e395a6a2ed3';
      //const publicKey = 'pk_test_ad0e2543e22474a4b2942e306363cf9f3360b30d';
      
      const reference = 'wallet_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);

      const handler = window.PaystackPop.setup({
        key: publicKey,
        email: user.email,
        amount: purchaseAmount * 100,
        currency: 'NGN',
        ref: reference,
        metadata: {
          user_id: user.id,
          user_email: user.email,
          purpose: 'coin_purchase'
        },
        callback: (response) => handlePaystackCallback(response),
        onClose: () => setPurchaseLoading(false)
      });

      handler.openIframe();
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Payment initiation failed');
      setPurchaseLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return <FontAwesomeIcon icon={faArrowUp} className="text-success" />;
      case 'session': return <FontAwesomeIcon icon={faArrowDown} className="text-danger" />;
      case 'session_hold': return <FontAwesomeIcon icon={faArrowDown} className="text-warning" />;
      case 'payment': return <FontAwesomeIcon icon={faArrowUp} className="text-primary" />;
      case 'gift': return <FontAwesomeIcon icon={faArrowUp} className="text-info" />;
      case 'transfer': return <FontAwesomeIcon icon={faArrowDown} className="text-secondary" />;
      case 'refund': return <FontAwesomeIcon icon={faArrowUp} className="text-primary" />;
      default: return <FontAwesomeIcon icon={faCoins} className="text-muted" />;
    }
  };

  const getTransactionBadge = (type) => {
    const variants = { 
      'purchase': 'success', 
      'session': 'danger', 
      'session_hold': 'warning',
      'payment': 'primary',
      'gift': 'info',
      'transfer': 'secondary',
      'refund': 'primary' 
    };
    const labels = { 
      'purchase': 'Purchase', 
      'session': 'Session Payment', 
      'session_hold': 'Session Hold',
      'payment': 'Payment',
      'gift': 'Gift',
      'transfer': 'Transfer',
      'refund': 'Refund' 
    };
    return <Badge bg={variants[type] || 'secondary'}>{labels[type] || type.replace('_', ' ')}</Badge>;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN'
  }).format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Safari-compatible date parsing
    const isoString = dateString.replace(' ', 'T');
    
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const safeToNumber = (value) => isNaN(parseFloat(value)) ? 0 : parseFloat(value);

  // Debug current state
  console.log('Current transactions state:', transactions);
  console.log('Current purchaseHistory state:', purchaseHistory);
  console.log('Current wallet state:', wallet);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" size="lg" />
        <p className="mt-3 text-muted">Loading your wallet...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          {success}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center h-100">
            <Card.Body className="d-flex flex-column">
              <FontAwesomeIcon icon={faWallet} size="3x" className="text-primary mb-3" />
              <h3 className="mb-2">
                {safeToNumber(wallet?.coins_balance).toFixed(2)}
                <small className="text-muted ms-2">Coins</small>
              </h3>
              <p className="text-muted mb-3">Current Balance</p>
              <div className="mt-auto">
                <Button variant="primary" onClick={() => setShowPurchaseModal(true)} className="w-100" disabled={purchaseLoading}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Buy Coins
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Card className="h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faHistory} className="me-2" />
                Wallet Activity
              </h5>
            </Card.Header>
            <Card.Body>
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                <Tab eventKey="transactions" title={
                  <span>
                    Transaction History
                    {transactions.length > 0 && (
                      <Badge bg="secondary" pill className="ms-2">
                        {transactions.length}
                      </Badge>
                    )}
                  </span>
                }>
                  {(() => {
                    console.log('Rendering transactions tab, length:', transactions.length);
                    console.log('Transactions array:', transactions);
                    return transactions.length === 0 ? (
                      <div className="text-center py-4">
                        <FontAwesomeIcon icon={faHistory} size="3x" className="text-muted mb-3" />
                        <p className="text-muted">No transactions yet</p>
                        <p className="text-muted small">Your session payments and other transactions will appear here</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Description</th>
                              <th>Amount</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((transaction) => (
                              <tr key={transaction.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {getTransactionIcon(transaction.transaction_type)}
                                    <span className="ms-2">{getTransactionBadge(transaction.transaction_type)}</span>
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <div>{transaction.description}</div>
                                    {transaction.reference_id && (
                                      <small className="text-muted">Ref: {transaction.reference_id}</small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className={
                                    ['purchase', 'payment', 'gift', 'refund'].includes(transaction.transaction_type) 
                                      ? 'text-success fw-bold' 
                                      : 'text-danger fw-bold'
                                  }>
                                    {['purchase', 'payment', 'gift', 'refund'].includes(transaction.transaction_type) ? '+' : '-'}
                                    {safeToNumber(transaction.coins_amount).toFixed(2)} Coins
                                  </span>
                                </td>
                                <td><small className="text-muted">{formatDate(transaction.created_at)}</small></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    );
                  })()}
                </Tab>

                <Tab eventKey="purchases" title={
                  <span>
                    Purchase History
                    {purchaseHistory.length > 0 && (
                      <Badge bg="success" pill className="ms-2">
                        {purchaseHistory.length}
                      </Badge>
                    )}
                  </span>
                }>
                  {purchaseHistory.length === 0 ? (
                    <div className="text-center py-4">
                      <FontAwesomeIcon icon={faCoins} size="3x" className="text-muted mb-3" />
                      <p className="text-muted">No coin purchases yet</p>
                      <p className="text-muted small">Your coin purchase history will appear here</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Purchase</th>
                            <th>Amount Paid</th>
                            <th>Coins Received</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchaseHistory.map((purchase) => (
                            <tr key={purchase.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faArrowUp} className="text-success me-2" />
                                  <Badge bg="success">Purchase</Badge>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div className="fw-bold">{formatCurrency(safeToNumber(purchase.amount_paid) / 100)}</div>
                               
                                </div>
                              </td>
                              <td>
                                <span className="text-success fw-bold">
                                  +{safeToNumber(purchase.coins_purchased).toFixed(2)} Coins
                                </span>
                              </td>
                              <td><small className="text-muted">{formatDate(purchase.created_at)}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showPurchaseModal} onHide={() => setShowPurchaseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCoins} className="me-2 text-primary" />
            Buy Coins
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="primary">
            <FontAwesomeIcon icon={faCoins} className="me-2" />
            <strong>Coin Rate:</strong> 1 Coin = ₦100
          </Alert>

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Select Amount</Form.Label>
              <Form.Select value={purchaseAmount} onChange={(e) => setPurchaseAmount(parseInt(e.target.value))} size="lg">
                <option value={1000}>₦1000 = 10 Coins</option>
                <option value={2000}>₦2000 = 20 Coins</option>
                <option value={5000}>₦5000 = 50 Coins</option>
                <option value={10000}>₦10,000 = 100 Coins</option>
                <option value={20000}>₦20,000 = 200 Coins</option>
                <option value={50000}>₦50,000 = 500 Coins</option>
              </Form.Select>
            </Form.Group>

            <Card className="bg-light">
              <Card.Body>
                <h6 className="mb-3">Purchase Summary</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span>Amount to Pay:</span>
                  <strong className="text-primary">{formatCurrency(purchaseAmount)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Coins to Receive:</span>
                  <strong className="text-success">{purchaseAmount / 100} Coins</strong>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span>New Balance:</span>
                  <strong>{(safeToNumber(wallet?.coins_balance) + (purchaseAmount / 100)).toFixed(2)} Coins</strong>
                </div>
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPurchaseModal(false)} disabled={purchaseLoading}>Cancel</Button>
          <Button variant="primary" onClick={handlePurchase} disabled={purchaseLoading || !paystackLoaded} size="lg">
            {purchaseLoading ? (
              <><Spinner animation="border" size="sm" className="me-2" />Processing Payment...</>
            ) : !paystackLoaded ? (
              <><Spinner animation="border" size="sm" className="me-2" />Loading Payment System...</>
            ) : (
              <>Pay {formatCurrency(purchaseAmount)}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Wallet;
